import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { ChevronUpIcon } from "@heroicons/react/20/solid";
import { motion, useInView } from "framer-motion";
import React, { useRef } from "react";
import { FaCheckCircle, FaClock, FaDollarSign, FaShieldAlt, FaUsers, FaCode } from "react-icons/fa";
import { STANDARD_VARIANTS, HOVER_ANIMATIONS } from "../constants/animations";
import { BUTTON_STYLES, CONTAINER_STYLES, TYPOGRAPHY_STYLES, CARD_STYLES } from "../constants/styles";

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: "pricing" | "process" | "technical" | "experience";
  icon: React.ComponentType<any>;
}

const faqs: FAQItem[] = [
  {
    id: 1,
    question: "What's your typical project timeline and process?",
    answer: "Every project is unique, and timelines vary dramatically based on your clarity of vision. If you have a clear plan and know exactly what you want, development moves fast. For more exploratory projects, we iterate and refine together. Typically: mobile apps take 2 weeks to 3 months, web applications 1 week to 2 months. I maintain transparent communication throughout with regular updates tailored to your needs.",
    category: "process",
    icon: FaClock,
  },
  {
    id: 2,
    question: "What's your pricing structure for different project types?",
    answer: "I offer flexible pricing to suit different needs: hourly rates ($150-250 based on complexity) for ongoing work, or fixed project quotes for well-defined scopes. I provide detailed estimates after understanding your requirements and always deliver what we agree upon.",
    category: "pricing",
    icon: FaDollarSign,
  },
  {
    id: 3,
    question: "How do you handle security and data protection?",
    answer: "Security is paramount in all my work. With my military cybersecurity background (8200 Unit), I implement robust security from day one: encrypted data transmission, secure authentication, and industry best practices. Your data and users are protected with enterprise-grade security measures.",
    category: "technical",
    icon: FaShieldAlt,
  },
  {
    id: 4,
    question: "Do you work with startups or only established companies?",
    answer: "I primarily work with startups and growing companies! From early-stage startups needing MVP development to established businesses requiring advanced solutions. I understand startup constraints and can work within budgets while delivering high-quality code that scales as you grow.",
    category: "experience",
    icon: FaUsers,
  },
  {
    id: 5,
    question: "What technologies do you specialize in, and do you stay current?",
    answer: "I'm a one-man show who can handle almost everything: Flutter and mobile development, web apps with React, Python/Node.js backends, AI integration, cybersecurity, and deployment. I choose the right technology for your specific needs, not what's trendy. I continuously adapt—recently pioneering Mobile MCP framework and working with latest AI technologies.",
    category: "technical",
    icon: FaCode,
  },
  {
    id: 6,
    question: "What happens after project delivery? Do you provide ongoing support?",
    answer: "I provide comprehensive support for all my projects. While initial bug fixes and adjustments are part of the delivery process, I offer ongoing maintenance packages for updates, scaling, and feature additions. I'm invested in your long-term success and many clients continue working with me as their technical partner.",
    category: "process",
    icon: FaCheckCircle,
  },
  {
    id: 7,
    question: "How do you communicate progress and handle changes during development?",
    answer: "I believe in transparent, flexible communication. Progress updates, access to staging environments for real-time testing, and regular check-ins keep you informed. Change requests are welcome and handled transparently with clear impact assessments on timeline and budget. I adapt my communication style to what works best for you.",
    category: "process",
    icon: FaUsers,
  },
];

const iconStyles = {
  primary: "text-primary-600 bg-primary-50 dark:bg-primary-100/20 dark:text-accent-100",
  secondary: "text-primary-50 bg-primary-600 dark:bg-primary-400/20 dark:text-accent-100 "
};

const FAQItemComponent: React.FC<{ faq: FAQItem; index: number }> = ({ faq, index }) => {
  return (
    <motion.div
      variants={STANDARD_VARIANTS.item}
      className={`${CARD_STYLES.base} overflow-hidden`}
    >
      <Disclosure>
        {({ open }) => (
          <>
            <DisclosureButton className="flex justify-between w-full px-6 py-6 text-left focus:outline-none focus-visible:ring focus-visible:ring-primary-500 focus-visible:ring-opacity-75">
              <div className="flex items-start space-x-4">
                <div className={`p-2 rounded-lg ${index % 2 === 0 ? iconStyles.primary : iconStyles.secondary}`}>
                  <faq.icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-accent-900 dark:text-surface-50 pr-4">
                    {faq.question}
                  </h3>
                </div>
              </div>
              <ChevronUpIcon
                className={`${open ? "rotate-180 transform" : ""
                  } h-6 w-6 text-accent-500 dark:text-accent-400 transition-transform duration-200 flex-shrink-0`}
              />
            </DisclosureButton>
            <DisclosurePanel className="px-6 pb-6">
              <div className="ml-16">
                <p className="text-accent-700 dark:text-accent-300 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </motion.div>
  );
};

const FAQ: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <motion.section
      ref={ref}
      className={CONTAINER_STYLES.section}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={STANDARD_VARIANTS.container}
    >
      <div className={CONTAINER_STYLES.content}>
        <motion.div variants={STANDARD_VARIANTS.item} className="text-center mb-16">
          <h2 className={TYPOGRAPHY_STYLES.sectionTitle}>
            Frequently Asked Questions
          </h2>
          <p className={TYPOGRAPHY_STYLES.sectionSubtitle}>
            Everything you need to know about working with me. Don't see your question?{" "}
            <a
              href="#contact"
              className="text-primary-600 dark:text-primary-400 hover:underline font-semibold"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Ask me directly
            </a>
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FAQItemComponent key={faq.id} faq={faq} index={index} />
          ))}
        </div>

        <motion.div
          variants={STANDARD_VARIANTS.item}
          className="text-center mt-16"
        >
          <div className="bg-surface-200 dark:bg-accent-800 rounded-xl p-8 border border-surface-300 dark:border-accent-700">
            <h3 className="text-2xl font-bold text-accent-900 dark:text-surface-50 mb-4">
              Still Have Questions?
            </h3>
            <p className="text-accent-600 dark:text-accent-400 mb-6">
              I'm here to help! Let's discuss your project requirements and how I can bring your vision to life.
            </p>
            <motion.button
              {...HOVER_ANIMATIONS.scale}
              className={BUTTON_STYLES.primary}
              onClick={() => {
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Schedule a Call
            </motion.button>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default FAQ;